import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'seamless-immutable';
import moment from 'moment';
import FileSaver from 'file-saver';

import DataTransformers from '../data-transformers/data-transformers';
import { LoadingIndicator } from '../../../components';
import WidgetWrapper from '../widget-wrapper';
import prepareVisualizerOptions from '../../../util/prepare-visualizer-options';
import formatNumber from '../../../util/format-number';
import sortData from '../../../util/sort-data';
import VisualizerSelector from '../data-visualizers/visualizer-selector';

const styles = {
  LoadingIndicatorWrapper: {
    minHeight: '100px',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
};

class Widget extends Component {
  constructor(props) {
    super(props);

    const {
      clientDataTransformer, clientDataTransformerOptions, clientDataVisualizer, clientDataVisualizerOptions, currencySymbol,
    } = props;

    const dataTransformer = DataTransformers.get(clientDataTransformer);
    const transformerOptions = JSON.parse(clientDataTransformerOptions);

    const visualizerOptions = prepareVisualizerOptions(clientDataVisualizerOptions, clientDataVisualizer, currencySymbol);
    const { alternatives } = visualizerOptions;

    this.state = {
      dataTransformer: dataTransformer ? dataTransformer() : null,
      transformerOptions,
      visualizerOptions,
      alternatives: alternatives || [],
    };
  }

  getSelectedAlternativeData(reportData, selectedAlternative) {
    if (!selectedAlternative || !selectedAlternative.key) {
      return reportData;
    }

    return reportData.map((r) => {
      const { alternativeData } = r;
      const data = alternativeData ? alternativeData[selectedAlternative.key] : [];

      return {
        ...r,
        data: data || [],
      };
    });
  }

  setNextAlternative = () => {
    const { alternatives } = this.state;

    if (alternatives.length < 2) {
      return;
    }

    this.setState({
      alternatives: alternatives.concat(alternatives.splice(0, 1)),
    });
  }

  dataIsEmpty(dataArray) {
    return dataArray && dataArray.every(data => data.length < 1);
  }

  fetchReportData(reportDataId, dateRange, locationId) {
    const {
      loadReportData, reportWidgetID,
    } = this.props;

    const nextDay = moment().add(1, 'days').set('hours', 5).set('minutes', 0)
      .startOf('minutes')
      .unix();

    const isFuture = dateRange.startDate >= nextDay;

    const payload = {
      reportDataId,
      startDate: isFuture
        ? moment.unix(dateRange.startDate).subtract(1, 'year').valueOf()
        : dateRange.startDate * 1000, // times thousand to get from seconds to milliseconds
      endDate: isFuture
        ? moment.unix(dateRange.endDate).subtract(1, 'year').valueOf()
        : dateRange.endDate * 1000,
      widgetId: reportWidgetID,
      options: {
        locationId,
      },
    };

    loadReportData(payload);
  }

  exportCsv(data, transformedData) {
    const { title, clientDataTransformer } = this.props;

    const csvLine = columns => `"${columns.join('";"')}"\r\n`;

    const d = clientDataTransformer === 'InvoiceServiceDataTransformer' ? transformedData[0] : data[0];

    let body = '';
    const hasName = !!d.data[0].name;

    body += csvLine((hasName ? ['Name'] : []).concat(d.columns));

    d.data.forEach((line) => { body += csvLine((hasName ? [line.name] : []).concat(line.values.map(x => formatNumber(x)))); });

    const BOM = '\uFEFF';
    const csvData = BOM + body;

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(blob, `${title}.csv`);
  }

  render() {
    const {
      title, width, container, reportData, isLoading, inApp, clientDataVisualizer, reportDataId, gridColumn, gridRow,
    } = this.props;

    const {
      dataTransformer, transformerOptions, visualizerOptions, alternatives,
    } = this.state;

    const selectedAlternative = alternatives[0];
    const mappedReportData = this.getSelectedAlternativeData(reportData, selectedAlternative);

    let content = null;
    let transformedData = null;

    if (isLoading) {
      content = (
        <div style={styles.LoadingIndicatorWrapper}>
          <LoadingIndicator />
        </div>
      );
    } else if (mappedReportData.length < 1) {
      content = <span>Leider sind keine Daten verfügbar</span>;
    } else if (dataTransformer) {
      const mutableMappedReportData = Immutable.asMutable(mappedReportData, { deep: true })
        .sort((a, b) => a.reportDataId - b.reportDataId);

      transformedData = dataTransformer.transformMultiple(
        mutableMappedReportData,
        transformerOptions,
      );
      if (this.dataIsEmpty(transformedData)) {
        content = <span>Für den gewählten Zeitraum sind keine Daten verfügbar</span>;
      } else {
        sortData(transformedData[0], visualizerOptions);

        content = (
          <VisualizerSelector
            visualizerName={clientDataVisualizer}
            data={transformedData}
            options={visualizerOptions}
            reportDataId={reportDataId}
          />
        );
      }
    } else {
      content = <span>DataTransformer konnte nicht gefunden werden</span>;
    }

    return (
      <WidgetWrapper
        title={title}
        container={container}
        width={width}
        inApp={inApp}
        visualizerOptions={visualizerOptions}
        setVisualizerOptions={newOptions => this.setState({ visualizerOptions: newOptions })}
        alternative={selectedAlternative ? selectedAlternative.name : null}
        onNextAlternative={this.setNextAlternative}
        exportCsv={() => this.exportCsv(mappedReportData, transformedData)}
        dataIsEmpty={isLoading || this.dataIsEmpty(transformedData)}
        gridColumn={gridColumn}
        gridRow={gridRow}
      >
        {content}
      </WidgetWrapper>
    );
  }
}

Widget.propTypes = {
  title: PropTypes.string,
  reportWidgetID: PropTypes.number.isRequired,
  width: PropTypes.number,
  container: PropTypes.number,
  clientDataTransformer: PropTypes.string,
  clientDataTransformerOptions: PropTypes.string,
  clientDataVisualizer: PropTypes.string,
  clientDataVisualizerOptions: PropTypes.string,
  isLoading: PropTypes.bool,
  inApp: PropTypes.bool,
  reportData: PropTypes.array,
  currencySymbol: PropTypes.string.isRequired,
  reportDataId: PropTypes.number,
  loadReportData: PropTypes.func.isRequired,
  gridColumn: PropTypes.number,
  gridRow: PropTypes.number,
};

Widget.defaultProps = {
  title: 'Widget',
  width: 12,
  container: 1,
  clientDataTransformer: '',
  clientDataTransformerOptions: '',
  clientDataVisualizer: '',
  clientDataVisualizerOptions: '',
  isLoading: false,
  reportData: [],
  inApp: false,
  reportDataId: null,
  gridColumn: null,
  gridRow: null,
};

export default Widget;
